import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Radar, Database, Bot, Layers, Gauge, ShieldAlert, Fingerprint,
  RefreshCw, Send, DollarSign, ArrowUpRight, Activity, Brain, MessageSquare, Cpu,
  ShieldCheck, CheckCircle2, Sprout, ArrowRight,
} from "lucide-react";
import api from "../lib/api";

const CATS = [
  { key: "source", label: "Sourcing", icon: Radar },
  { key: "enrichment", label: "Enrichment", icon: Layers },
  { key: "valuation", label: "Valuation", icon: DollarSign },
  { key: "qualification", label: "Qualification", icon: Gauge },
  { key: "motivation", label: "Motivation", icon: Activity },
  { key: "identity", label: "Identity", icon: Fingerprint },
  { key: "freshness", label: "Freshness", icon: RefreshCw },
  { key: "outreach", label: "Outreach", icon: Send },
];

// The live acquisition architecture — the AI Buy Box drives real data providers, not AI guesswork.
const PIPELINE = [
  { icon: Radar, label: "AI Buy Box", sub: "Your criteria" },
  { icon: Database, label: "Data Providers", sub: "RealEstateAPI + PropertyRadar" },
  { icon: ShieldCheck, label: "County GIS", sub: "APN + parcel verify" },
  { icon: Send, label: "Auto-Promote", sub: "Verified parcels only" },
  { icon: CheckCircle2, label: "Leads", sub: "Ready to work" },
];

function Stat({ label, value }) {
  return (
    <div className="text-center px-4" data-testid={`office-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-2xl font-bold text-primary tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

export default function AIOffice() {
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    document.title = "ARQITEK — AI Office";
    api.get("/acq/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/acq/agents").then((r) => setAgents(r.data.agents || [])).catch(() => {});
  }, []);

  const activeAgents = agents.filter((a) => a.active_in_architecture);
  const catCount = (k) => activeAgents.filter((a) => a.category === k).length;
  const catActive = (k) => activeAgents.filter((a) => a.category === k).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="ai-office-page">
      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-primary mb-2">AI Command Center</span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">AI Office</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Arqitek's acquisition department. Your <span className="text-foreground font-medium">AI Buy Box</span> pulls
          real land directly from RealEstateAPI (active) and PropertyRadar (off-market), verifies every parcel against
          county GIS, and drops the verified matches straight into Leads — on demand or on a schedule. No AI guesswork.
        </p>
      </div>

      {/* Live acquisition architecture */}
      <div className="rounded-2xl border border-border bg-card p-5 sm:p-6" data-testid="ai-office-pipeline">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="text-sm font-semibold">How leads are generated now</div>
          <Link to="/ai-buybox" className="text-xs font-medium text-primary inline-flex items-center gap-1 hover:underline" data-testid="pipeline-open-buybox">
            Open AI Buy Box <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-stretch gap-2">
          {PIPELINE.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="flex items-center gap-2 flex-1">
                <div className="flex-1 rounded-xl border border-border bg-background/60 p-3" data-testid={`pipeline-step-${i}`}>
                  <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center mb-2">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="text-sm font-semibold leading-tight">{s.label}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{s.sub}</div>
                </div>
                {i < PIPELINE.length - 1 && (
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 rotate-90 sm:rotate-0" />
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          APN conflicts are flagged and skipped — never auto-promoted. Broad AI discovery and automatic per-lead AI
          enrichment are turned <span className="text-foreground font-medium">OFF</span> to control cost; leads carry
          real provider + GIS data.
        </p>
      </div>

      {/* Animated office floor */}
      <div
        className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-b from-[#0b1512] to-[#060d0b] p-6 sm:p-10"
        data-testid="ai-office-floor"
      >
        {/* grid texture */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(#37d0a8 1px, transparent 1px), linear-gradient(90deg, #37d0a8 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        <div className="relative flex flex-col items-center">
          {/* Angela core */}
          <div className="relative flex items-center justify-center mb-10">
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="absolute rounded-full border border-primary/40"
                style={{ width: 90 + i * 46, height: 90 + i * 46 }}
                animate={{ scale: [1, 1.12, 1], opacity: [0.5, 0.15, 0.5] }}
                transition={{ duration: 3.2, repeat: Infinity, delay: i * 0.5, ease: "easeInOut" }}
              />
            ))}
            <motion.div
              className="relative z-10 h-20 w-20 rounded-full bg-primary/15 border border-primary/50 flex items-center justify-center"
              animate={{ boxShadow: ["0 0 0 0 rgba(55,208,168,0.0)", "0 0 32px 4px rgba(55,208,168,0.35)", "0 0 0 0 rgba(55,208,168,0.0)"] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <Bot className="h-9 w-9 text-primary" />
            </motion.div>
          </div>
          <div className="text-center -mt-4 mb-8">
            <div className="text-sm font-semibold">Acquisition Engine</div>
            <div className="text-xs text-muted-foreground">
              {activeAgents.length > 0
                ? `${activeAgents.length} agent${activeAgents.length === 1 ? "" : "s"} on standby`
                : "No agents — clean slate"}
            </div>
          </div>

          {activeAgents.length === 0 ? (
            <div className="w-full rounded-xl border border-dashed border-white/15 bg-white/[0.03] p-6 text-center" data-testid="office-floor-empty">
              <p className="text-[12px] text-white/55 max-w-lg mx-auto">
                No agents right now — the roster was cleared for a clean slate. Lead generation still runs on the data
                pipeline above (RealEstateAPI + PropertyRadar + county GIS). Create a new agent when you're ready.
              </p>
            </div>
          ) : (
          <>
          {/* Agent desks by department */}
          <div className="mb-4 text-center">
            <p className="text-[11px] text-white/45 max-w-xl mx-auto">
              Specialist agents are on standby for on-demand parcel research and enrichment. They no longer run
              automatically on every lead — lead generation runs on the data pipeline above.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 w-full">
            {CATS.map((c, idx) => {
              const Icon = c.icon;
              const total = catCount(c.key);
              const active = catActive(c.key);
              if (!total) return null;
              return (
                <motion.div
                  key={c.key}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.08, duration: 0.5 }}
                  className="relative rounded-xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-sm"
                  data-testid={`office-dept-${c.key}`}
                >
                  <motion.div
                    className="absolute right-3 top-3 h-2 w-2 rounded-full bg-primary"
                    animate={{ opacity: [1, 0.25, 1] }}
                    transition={{ duration: 1.6, repeat: Infinity, delay: idx * 0.2 }}
                  />
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="text-sm font-semibold text-white">{c.label}</div>
                  <div className="text-[11px] text-white/50 mt-0.5">
                    {total} active agent{total === 1 ? "" : "s"}
                  </div>
                </motion.div>
              );
            })}
          </div>
          </>
          )}
        </div>
      </div>

      {/* Live counters */}
      <div className="flex flex-wrap items-center justify-center gap-y-4 rounded-xl border border-border bg-card py-5" data-testid="ai-office-stats">
        <Stat label="Active Agents" value={stats?.agents_active_architecture ?? "—"} />
        <div className="h-8 w-px bg-border" />
        <Stat label="Data Sources" value={stats?.data_sources_active ?? "—"} />
        <div className="h-8 w-px bg-border" />
        <Stat label="Leads" value={stats?.leads_total ?? 0} />
      </div>

      {/* Navigation cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link
          to="/ai-buybox"
          className="group rounded-xl border border-primary/40 bg-primary/[0.04] p-5 hover:border-primary/70 transition-colors sm:col-span-2"
          data-testid="office-nav-buybox"
        >
          <div className="flex items-start justify-between">
            <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center">
              <Sprout className="h-5 w-5 text-primary" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <div className="mt-3 font-semibold">AI Buy Box — start here</div>
          <p className="text-sm text-muted-foreground mt-1">
            The single control surface for lead generation. Set your markets and criteria, then Run Now or schedule it —
            RealEstateAPI + PropertyRadar + county GIS do the rest and drop verified parcels into Leads.
          </p>
        </Link>

        <Link
          to="/ai-office/agents"
          className="group rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors"
          data-testid="office-nav-agents"
        >
          <div className="flex items-start justify-between">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Bot className="h-5 w-5 text-primary" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <div className="mt-3 font-semibold">Agent Command Center</div>
          <p className="text-sm text-muted-foreground mt-1">
            {stats?.agents_active_architecture
              ? `The ${stats.agents_active_architecture} agents that run the current architecture.`
              : "No agents yet — the roster is cleared. Create a new agent to get started."}
          </p>
        </Link>

        <Link
          to="/ai-office/data-sources"
          className="group rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors"
          data-testid="office-nav-data-sources"
        >
          <div className="flex items-start justify-between">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Database className="h-5 w-5 text-primary" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <div className="mt-3 font-semibold">Data Sources</div>
          <p className="text-sm text-muted-foreground mt-1">
            The live feeds behind the pipeline — RealEstateAPI, PropertyRadar and county GIS — with honest health.
          </p>
        </Link>

        <Link
          to="/ai-office/brief"
          className="group rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors"
          data-testid="office-nav-brief"
        >
          <div className="flex items-start justify-between">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Activity className="h-5 w-5 text-primary" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <div className="mt-3 font-semibold">Daily Acquisition Brief</div>
          <p className="text-sm text-muted-foreground mt-1">
            A morning read on the pipeline — top opportunities and what needs attention.
          </p>
        </Link>

        <Link
          to="/ai-office/learning"
          className="group rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors"
          data-testid="office-nav-learning"
        >
          <div className="flex items-start justify-between">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Brain className="h-5 w-5 text-primary" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <div className="mt-3 font-semibold">Learning Loop</div>
          <p className="text-sm text-muted-foreground mt-1">
            Learns from the leads you accept and reject to sharpen deal scoring over time.
          </p>
        </Link>

        <Link
          to="/ai-office/executions"
          className="group rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors"
          data-testid="office-nav-executions"
        >
          <div className="flex items-start justify-between">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Cpu className="h-5 w-5 text-primary" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <div className="mt-3 font-semibold">AI Execution Log</div>
          <p className="text-sm text-muted-foreground mt-1">
            Every LLM call with provider, model, tokens, cost, latency, fallback and the full prompt/response — plus model routing and performance.
          </p>
        </Link>

        <Link
          to="/ai-comms?channels=1"
          className="group rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors"
          data-testid="office-nav-comms"
        >
          <div className="flex items-start justify-between">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-primary" />
            </div>
            <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <div className="mt-3 font-semibold">Communication</div>
          <p className="text-sm text-muted-foreground mt-1">
            The three outreach channels — Postmark email, Twilio SMS, and PostGrid direct mail.
          </p>
        </Link>
      </div>

      <div className="rounded-xl border border-dashed border-border bg-card/40 p-4 flex items-start gap-3" data-testid="office-phase-note">
        <ShieldAlert className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground">
          Leads come from real data only — RealEstateAPI + PropertyRadar sourcing, verified against county GIS.
          Broad AI discovery and automatic per-lead AI enrichment are <span className="text-foreground font-medium">OFF</span> to
          control cost, and APN conflicts are flagged and skipped rather than promoted. Outreach still runs on real,
          verified contacts only — email via Postmark, SMS via Twilio (consent required), direct mail via PostGrid.
        </p>
      </div>
    </div>
  );
}
